import { createContext, useContext, useState, useEffect } from 'react';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  getIdTokenResult 
} from 'firebase/auth';
import { auth } from './firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Login function
  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  // Signup function
  function signup(email, password) {
    return createUserWithEmailAndPassword(auth, email, password);
  }

  // Logout function
  function logout() {
    return signOut(auth);
  }

  // Check for admin claims in the Firebase token
  async function checkUserClaims(user) {
    if (!user) return null;
    
    try {
      const tokenResult = await getIdTokenResult(user);
      // Add admin property to user object if it exists in claims
      return {
        ...user,
        admin: tokenResult.claims.admin === true,
        getIdToken: user.getIdToken.bind(user)
      };
    } catch (error) {
      console.error("Error checking claims:", error);
      return user;
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const userWithClaims = await checkUserClaims(user);
      setCurrentUser(userWithClaims);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    login,
    signup,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}